import {
  Breadcrumb,
  Divider,
  Empty,
  Input,
  Layout,
  Select,
  Skeleton,
  Space,
  Table,
  notification,
  theme,
} from "antd";
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { apiAdminSearchCheckouts } from "../../../../api/events";
import { formatDateTime, formatPrice } from "../../../../utils/format";
import "./EventCheckoutSearch.scss";

const { Content } = Layout;

const statusOptions = [
  { value: "PAID", label: "Pago" },
  { value: "FREE", label: "Gratuito" },
  { value: "PENDING", label: "Pendente" },
  { value: "EXPIRED", label: "Expirado" },
  { value: "CANCELED", label: "Cancelado" },
  { value: "", label: "Todos" },
];

const EventCheckoutSearch = () => {
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    total: 0,
    pageSize: 10,
  });

  const debounceTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const statusTranslations = {
    FREE: "Gratuito",
    EXPIRED: "Expirado",
    PENDING: "Pendente",
    PAID: "Pago",
    CANCELED: "Cancelado",
  };

  const handleSimulate = async (page, search, status) => {
    setLoading(true);
    try {
      const filters = { page, page_size: pagination.pageSize };
      if (search) {
        filters.search = search;
      }
      if (status) {
        filters.status = status;
      }
      const json = await apiAdminSearchCheckouts(filters);
      setData(json.results || []);
      setPagination((prevPagination) => ({
        ...prevPagination,
        total: json.count || 0,
        current: page,
      }));
    } catch (error) {
      notification.error({
        message: "Erro",
        description:
          error?.status === 500
            ? "Ocorreu um erro no servidor ao buscar as compras. Por favor, tente novamente mais tarde."
            : "Ocorreu um erro ao buscar as compras.",
        placement: "topRight",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    setSearchText(value);
    if (value === "") {
      setHasSearched(false);
      setData([]);
      setPagination((prevPagination) => ({
        ...prevPagination,
        current: 1,
        total: 0,
      }));
      return;
    }
    setHasSearched(true);
    handleSimulate(1, value, statusFilter);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    debounceTimeoutRef.current = setTimeout(() => {
      handleSearch(value);
    }, 300);
  };

  const handleStatusChange = (value) => {
    const status = value || "";
    setStatusFilter(status);
    if (searchText !== "") {
      handleSimulate(1, searchText, status);
    }
  };

  const handleTableChange = (paginationInfo) => {
    handleSimulate(paginationInfo.current, searchText, statusFilter);
  };

  const columns = [
    {
      title: "Comprador",
      dataIndex: ["customer", "name"],
      key: "customer_name",
    },
    {
      title: "Email",
      dataIndex: ["customer", "email"],
      key: "customer_email",
      responsive: ["lg"],
    },
    {
      title: "Evento",
      dataIndex: ["event", "name"],
      key: "event_name",
    },
    {
      title: "Responsável",
      dataIndex: ["event", "account", "display_name"],
      key: "account_display_name",
      responsive: ["md"],
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => statusTranslations[status],
    },
    {
      title: "Valor",
      dataIndex: "total",
      key: "total",
      render: (total) => formatPrice(total / 100),
    },
    {
      title: "Data",
      dataIndex: "created_at",
      key: "created_at",
      render: formatDateTime,
    },
    {
      title: "",
      key: "action",
      render: (_, record) => (
        <Link to={`/eventos/${record.event?.account?.id}/${record.event?.id}/compras/${record.id}`}>
          Visualizar
        </Link>
      ),
    },
  ];

  const locale = {
    emptyText: hasSearched ? (
      <Empty description="Sem dados" />
    ) : (
      <Empty description="Busque por nome ou email do comprador" />
    ),
  };

  return (
    <div className="EventCheckoutSearch">
      <Layout
        style={{
          padding: "1.25rem",
        }}
      >
        <Breadcrumb
          style={{
            margin: "16px 0",
          }}
        >
          <div style={{ display: "flex", width: "100%", justifyContent: "space-between", alignItems: "center" }}>
            <h2 className="title-page">Buscar Compras</h2>
          </div>
          <Divider />
          <Breadcrumb.Item>Compras</Breadcrumb.Item>
        </Breadcrumb>
        <Content
          style={{
            padding: 24,
            margin: 0,
            minHeight: 280,
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
          }}
        >
          <div className="box-divider">
            <h2 className="subtitle-page">Buscar compras</h2>
          </div>
          <Divider />
          <Space className="filters" style={{ marginBottom: 16 }}>
            <Input.Search
              placeholder="Buscar por nome ou email do comprador"
              allowClear
              enterButton
              onSearch={handleSearch}
              onChange={handleInputChange}
              style={{ maxWidth: 320 }}
            />
            <Select
              placeholder="Filtrar por status"
              onChange={handleStatusChange}
              allowClear
              value={statusFilter}
              style={{ width: 200 }}
              options={statusOptions}
            />
          </Space>
          {loading ? (
            <Skeleton active />
          ) : (
            <Table
              columns={columns}
              locale={locale}
              dataSource={data.map((item) => ({ ...item, key: item.id }))}
              pagination={
                hasSearched
                  ? {
                      current: pagination.current,
                      total: pagination.total,
                      pageSize: pagination.pageSize,
                      showSizeChanger: false,
                    }
                  : false
              }
              bordered
              onChange={handleTableChange}
            />
          )}
        </Content>
        <Divider />
      </Layout>
    </div>
  );
};

export default EventCheckoutSearch;
